import { useEffect, useRef, useState, useCallback } from 'preact/hooks'
import type { EmailPreviewResponseDto } from '@/api/asset'
import { stringifyApiError } from '@/utils/errorHandling'

/**
 * Pulls human-readable messages out of the API's error string.
 *
 * Shapes we see in practice:
 * - `[{"Title":"...","Message":"...","FieldName":null}, …]` — BusinessRuleException JSON
 * - `{"Message":"..."}` — single error
 * - Plain string — already friendly, return as-is
 *
 * If parsing fails on something that looked like JSON, fall through and return the raw
 * trimmed string — the surface UI shows it as-is, which is good enough for an unexpected
 * server error shape.
 *
 * Takes `unknown` rather than `string` on purpose (ORB-537). apiClient normalizes `error` to text
 * now, but this function parses untrusted server output, and trusting its own signature is what
 * threw `raw.trim is not a function` when the API's real-array error shape reached it.
 */
export function formatPreviewError(raw: unknown): string {
    const trimmed = stringifyApiError(raw).trim()
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return trimmed
    try {
        const parsed = JSON.parse(trimmed) as unknown
        if (Array.isArray(parsed)) {
            const messages = parsed
                .map(e => {
                    if (e && typeof e === 'object' && 'Message' in e) {
                        return String((e as { Message?: unknown }).Message ?? '')
                    }
                    return ''
                })
                .filter(Boolean)
            if (messages.length > 0) return messages.join(' • ')
        }
        if (parsed && typeof parsed === 'object' && 'Message' in parsed) {
            return String((parsed as { Message?: unknown }).Message ?? trimmed)
        }
    } catch {
        // Deliberate silence: a parse failure on a JSON-shaped string just means the
        // server returned an unexpected shape (e.g., a plain error message that happens
        // to start with `[` or `{`). The raw trimmed string is already the right thing
        // to show — it falls through to the preview's error banner, where the user sees
        // the actual server text. There is no error to log because nothing is lost.
    }
    return trimmed
}

type Fetcher = (signal: AbortSignal) => Promise<{ data?: EmailPreviewResponseDto; error?: string }>

interface UseEmailPreviewFetchOptions {
    /** Optional debounce so rapid prop changes (e.g., live keystrokes via htmlOverride)
     *  coalesce into a single request. Template previews skip this. */
    debounceMs?: number
}

interface UseEmailPreviewFetchResult {
    preview: EmailPreviewResponseDto | null
    error: string | null
    loading: boolean
    /** Force a re-fetch with the current deps. Powers the refresh button. */
    refresh: () => void
}

/**
 * Fetches an email preview (template or copy), with AbortController-based cancellation
 * of in-flight requests when deps change and optional debouncing. The fetcher closure is
 * the only place the two preview surfaces differ — everything else (state shape, abort
 * handling, refresh tick) is shared.
 *
 * `depsKey` should change whenever the consumer wants a fresh fetch. Pass a stable string
 * combining the actual inputs (asset id, htmlOverride, etc.) and any external refreshKey.
 */
export function useEmailPreviewFetch(
    fetcher: Fetcher,
    depsKey: string,
    { debounceMs }: UseEmailPreviewFetchOptions = {},
): UseEmailPreviewFetchResult {
    const [preview, setPreview] = useState<EmailPreviewResponseDto | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [tick, setTick] = useState(0)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const abortRef = useRef<AbortController | null>(null)

    // Fetcher identity changes on every render in practice (closure over props), but the
    // depsKey + tick handle invalidation, so we don't want the effect to re-fire on
    // fetcher reference churn alone. Keep the latest fetcher in a ref instead.
    const fetcherRef = useRef(fetcher)
    fetcherRef.current = fetcher

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current)
        if (abortRef.current) abortRef.current.abort()

        const run = async () => {
            const controller = new AbortController()
            abortRef.current = controller
            setLoading(true)
            setError(null)

            const response = await fetcherRef.current(controller.signal)

            if (controller.signal.aborted) return

            if (response.data) {
                setPreview(response.data)
                setError(null)
            } else if (response.error) {
                setError(formatPreviewError(response.error) || 'Could not render preview.')
            }
            setLoading(false)
        }

        if (debounceMs && debounceMs > 0) {
            timerRef.current = setTimeout(() => { void run() }, debounceMs)
        } else {
            void run()
        }

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
            if (abortRef.current) abortRef.current.abort()
        }
    }, [depsKey, tick, debounceMs])

    const refresh = useCallback(() => setTick(t => t + 1), [])

    return { preview, error, loading, refresh }
}
